import { apiClient } from '../../utils/apiClient';
import { handleApiError } from '../../utils/errorHandler';

// Cache for document types to avoid repeated API calls
let documentTypesCache = null;
let medicalDocumentTypesCache = null;
let isLoading = false;

/**
 * Fetch document types from backend
 */
const fetchDocumentTypes = async () => {
  if (documentTypesCache && medicalDocumentTypesCache) {
    return { documentTypes: documentTypesCache, medicalDocumentTypes: medicalDocumentTypesCache };
  }

  if (isLoading) {
    // Wait for ongoing request to complete
    while (isLoading) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return { documentTypes: documentTypesCache, medicalDocumentTypes: medicalDocumentTypesCache };
  }

  isLoading = true;
  try {
    const response = await apiClient.get('documents/types');
    documentTypesCache = response.data.documentTypes;
    medicalDocumentTypesCache = response.data.medicalDocumentTypes;
    
    return { 
      documentTypes: documentTypesCache, 
      medicalDocumentTypes: medicalDocumentTypesCache 
    };
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    const errorInfo = handleApiError(error);
    console.error('Failed to fetch document types:', errorInfo);
    
    // Fallback to empty arrays if backend call fails
    return { documentTypes: [], medicalDocumentTypes: [] };
  } finally {
    isLoading = false;
  }
};

/**
 * Check if a document type requires medical stamp validation
 */
const requiresStampValidation = (documentType) => {
  // Use cached data if available, otherwise return false as fallback
  if (medicalDocumentTypesCache) {
    return medicalDocumentTypesCache.includes(documentType);
  }
  
  // Fallback behavior - trigger async fetch but return false for now
  // Console log removed for production
  return false;
};

/**
 * Validate hospital stamp on a medical document
 */
const validateDocumentStamp = async (documentId, isValid, notes) => {
  // Console log removed for production
  
  // Check if document type requires validation on frontend
  // Console log removed for production
  
  try {
    const response = await apiClient.put(`documents/${documentId}/validate-stamp`, {
      isValid,
      notes
    });
    
    return response.data;
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    const errorInfo = handleApiError(error);
    console.error('Failed to validate document stamp:', errorInfo);
    
    // Add specific debugging for stamp validation errors
    if (error.response?.data?.error?.includes('does not require stamp validation')) {
      // Document does not require stamp validation
    }
    
    // Create standardized error object
    throw {
      message: errorInfo.message,
      code: errorInfo.code
    };
  }
};

/**
 * Override stamp validation (Approver role only)
 */
const overrideStampValidation = async (documentId, isValid, notes) => {
  try {
    const response = await apiClient.put(`documents/${documentId}/override-stamp-validation`, {
      isValid,
      notes
    });
    return response.data;
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    const errorInfo = handleApiError(error);
    console.error('Failed to override stamp validation:', errorInfo);
    
    // Create standardized error object
    throw {
      message: errorInfo.message,
      code: errorInfo.code
    };
  }
};

// Upload document for claim
const uploadDocument = async (claimId, file, documentType) => {
  // Console log removed for production
  // Console log removed for production
  // Console log removed for production
  // Console log removed for production
  
  const formData = new FormData();
  formData.append('documents', file);
  formData.append('documentType', documentType);
  
  const uploadUrl = `documents/claim/${claimId}`;
  
  try {
    // Use the apiClient post method which handles FormData correctly
    const response = await apiClient.post(uploadUrl, formData);
    return response.data;
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    // Specifically detect file-too-large (HTTP 413) and invalid-type (HTTP 415)
    const errorInfo = handleApiError(error);
    
    // Throw the processed error so calling components can render friendly feedback
    const processedError = new Error(errorInfo.message);
    processedError.code = errorInfo.code;
    processedError.originalError = error;
    throw processedError;
  }
};

// Get documents by claim ID
const getDocumentsByClaimId = async (claimId) => {
  try {
    const response = await apiClient.get(`documents/claim/${claimId}`);
    const data = response.data;
    
    // Backend returns documents in response.documents
    return data.documents;
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    const errorInfo = handleApiError(error);
    console.error(`Error in documentService.getDocumentsByClaimId for ${claimId}:`, errorInfo);
    
    // Return empty array instead of throwing error when documents can't be found
    return [];
  }
};

// Delete document
const deleteDocument = async (documentId) => {
  try {
    const response = await apiClient.delete(`documents/${documentId}`);
    return response.data;
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    const errorInfo = handleApiError(error);
    const processedError = new Error(errorInfo.message);
    processedError.code = errorInfo.code;
    throw processedError;
  }
};

// Download document
const downloadDocument = async (documentId) => {
  try {
    // Get the pre-signed URL from the backend
    const response = await apiClient.get(`documents/${documentId}/download`);
    
    // The response now contains a JSON object with downloadUrl
    const { downloadUrl, fileName } = response.data;
    
    // Fetch the file using the pre-signed URL
    const fileResponse = await fetch(downloadUrl);
    if (!fileResponse.ok) {
      throw new Error(`Failed to download file: ${fileResponse.statusText}`);
    }
    
    const blob = await fileResponse.blob();
    
    return { 
      data: blob,
      fileName: fileName 
    };
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    const errorInfo = handleApiError(error);
    console.error('Failed to download document:', errorInfo);
    
    // Create standardized error object
    throw {
      message: errorInfo.message,
      code: errorInfo.code
    };
  }
};

// Get all documents
const getAllDocuments = async () => {
  try {
    const response = await apiClient.get('documents/');
    const data = response.data;
    
    // Backend returns documents in response.documents with pagination
    return {
      documents: data.documents,
      pagination: data.pagination
    };
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    const errorInfo = handleApiError(error);
    console.error('Failed to fetch all documents:', errorInfo);
    
    // Create standardized error object
    throw {
      message: errorInfo.message,
      code: errorInfo.code
    };
  }
};

// Get document by ID
const getDocumentById = async (documentId) => {
  try {
    const response = await apiClient.get(`documents/${documentId}`);
    return response.data;
  } catch (error) {
    // Task 3.2: Use handleApiError for consistent error handling
    const errorInfo = handleApiError(error);
    console.error('Failed to fetch document by ID:', errorInfo);
    
    // Create standardized error object
    throw {
      message: errorInfo.message,
      code: errorInfo.code
    };
  }
};

// Get document preview URL
const getDocumentPreviewUrl = (documentId) => {
  const baseUrl = import.meta.env.VITE_API_URL|| 'http://localhost:3000/api';
  return `${baseUrl}/documents/${documentId}/preview`;
};

/**
 * Initialize document types (call this on app startup)
 */
const initializeDocumentTypes = async () => {
  try {
    await fetchDocumentTypes();
  } catch {
    // Error initializing document types - will use fallback
  }
};

/**
 * Get document types for dropdowns and validation
 */
const getDocumentTypes = async () => {
  const { documentTypes } = await fetchDocumentTypes();
  return documentTypes;
};

// Helper function to get document type label
export const getDocumentTypeLabel = (type) => {
  // Use cached data if available
  if (documentTypesCache) {
    const docType = documentTypesCache.find(dt => dt.value === type);
    return docType ? docType.label : type;
  }
  
  // Fallback behavior - trigger async fetch but return formatted type for now
  // Console log removed for production
  
  // Return a formatted version of the type as fallback
  return type ? type.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ') : type;
};

const documentService = {
  uploadDocument,
  getDocumentsByClaimId,
  downloadDocument,
  deleteDocument,
  getAllDocuments,
  getDocumentById,
  getDocumentPreviewUrl,
  getDocumentTypeLabel,
  getDocumentTypes,
  fetchDocumentTypes,
  initializeDocumentTypes,
  requiresStampValidation,
  validateDocumentStamp,
  overrideStampValidation
};

export { requiresStampValidation, validateDocumentStamp, overrideStampValidation, fetchDocumentTypes, getDocumentTypes, initializeDocumentTypes, getDocumentPreviewUrl };
export default documentService;
